import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { EpodConfirmPopupComponent } from '@core/controls/_dialogs/epod-confirm-popup/epod-confirm-popup.component';
import { ConfirmationDialogResult } from '@core/models/dialogs/dialog-results.model';
import { DictionaryModel } from '@core/models/dictionary.model';
import { LanguageService } from '@core/services/language.service';
import { AddAbsenceDialogDataModel } from '../../models/add-absence-dialog-data.model';
import { AddAbsenceDialogResponseModel } from '../../models/add-absence-dialog-response.model';


@Component({
  selector: 'add-absence-for-planner',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, CommonModule, ReactiveFormsModule, MatDatepickerModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule],
  templateUrl: './add-absence-for-planner.component.html',
  styleUrl: './add-absence-for-planner.component.scss',
  providers: [provideNativeDateAdapter()]
})
export class AddAbsenceForPlannerComponent implements OnInit {
  scheduleStatusOptionsList!: DictionaryModel[];
  scheduleTypeOptionsList!: DictionaryModel[];

  private readonly destroyRef = inject(DestroyRef);
  private readonly _dialogRef = inject(MatDialogRef<EpodConfirmPopupComponent, ConfirmationDialogResult>);
  fb = inject(FormBuilder);

  addScheduleForm = this.fb.group({
    dateStart: this.fb.control<Date | null>(null, [Validators.required]),
    dateEnd: this.fb.control<Date | null>(null, [Validators.required]),
    scheduleStatus: this.fb.control<number | null>(null, [Validators.required]),
    scheduleType: this.fb.control<number | null>(null, [Validators.required]),
    description: this.fb.control<string>(''),
  });

  constructor(@Inject(MAT_DIALOG_DATA) public readonly dialogData: AddAbsenceDialogDataModel,
    private readonly languageService: LanguageService,
    private readonly dateAdapter: DateAdapter<any>,) {
    this.scheduleStatusOptionsList = dialogData.inaccessibilityStatus;
    this.scheduleTypeOptionsList = dialogData.inaccessibilityType;

    this.addScheduleForm.patchValue({
      scheduleStatus: this.scheduleStatusOptionsList[0].Id,
      scheduleType: this.scheduleTypeOptionsList[0].Id,
      dateStart: this.dialogData.startDate,
      dateEnd: this.dialogData.startDate,
    });
  }

  ngOnInit(): void {
    this.languageService.language$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => {
        this.dateAdapter.setLocale(language);
      });
  }

  confirmButtonClick(): void {
    if (this.addScheduleForm.valid) {
      const response: AddAbsenceDialogResponseModel = {
        absenceData: {
          dateStart: this.addScheduleForm.value.dateStart!,
          dateEnd: this.addScheduleForm.value.dateEnd!,
          description: this.addScheduleForm.value.description || '',
          scheduleStatus: this.addScheduleForm.value.scheduleStatus!,
          scheduleType: this.addScheduleForm.value.scheduleType!,
        },
        objectType: this.dialogData.objectType
      }

      this._dialogRef.close(response);
    }
  }

  cancelButtonClick(): void {
    this._dialogRef.close();
  }
}
